import { createRouter, createWebHistory } from 'vue-router'
import Home from '../pages/Home.vue'
import Search from '../pages/Search.vue'
import Ticket from '../pages/Ticket.vue'
import Trip from '../pages/Trip.vue'
import Company from '../pages/Company.vue'
import Download from '../pages/Download.vue'
import About from '../pages/About.vue'
import Auth from '../pages/User/Auth.vue'
import Admin from '../pages/Admin.vue'
import Account from '../pages/User/Index.vue'
import AccountProfile from '../pages/User/Profile.vue'
import AccountWallet from '../pages/User/Wallet.vue'
import AccountDriver from '../pages/User/Driver.vue'
import DesignSystem from '../pages/DesignSystem.vue'
import Faq from '../pages/Faq.vue'
import { useUserStore } from '@/stores/userStore'

const DEFAULT_TITLE = 'EasyGO'
const DEFAULT_DESCRIPTION = 'Réservez vos billets de bus et voyages interurbains au Cameroun en ligne avec EasyGO.'
const SITE_URL = 'https://easygo-voyages.com'

const routes = [
  {
    path: '/',
    name: 'home',
    component: Home,
    meta: {
      title: 'Accueil',
      description: 'Réservez vos billets de bus au Cameroun en ligne. Simple, rapide et sécurisé. Comparez les prix et trouvez votre trajet idéal.',
      keywords: 'réservation bus cameroun, billet bus en ligne, voyage interurbain cameroun'
    }
  },
  {
    path: '/search',
    name: 'search',
    component: Search,
    meta: {
      title: 'Recherche de trajets',
      description: 'Trouvez et comparez les meilleurs trajets de bus au Cameroun. Recherchez par ville de départ et destination.',
      keywords: 'recherche bus cameroun, comparer prix bus, trouver voyage'
    }
  },
  {
    path: '/search/start/:startCity',
    name: 'searchFromCity',
    component: Search,
    meta: {
      title: 'Recherche - Départ',
      description: 'Search results page with starting city',
      keywords: 'recherche bus départ, trouver bus depuis ville, voyage interurbain départ'
    }
  },
  {
    path: '/search/arrived/:endCity',
    name: 'searchToCity',
    component: Search,
    meta: {
      title: 'Recherche - Arrivée',
      description: 'Search results page with destination city',
      keywords: 'recherche bus arrivée, trouver bus vers ville, voyage interurbain destination'
    }
  },
  {
    path: '/search/:startCity/:endCity',
    name: 'searchFromToCity',
    component: Search,
    meta: {
      title: 'Recherche - Départ et Arrivée',
      description: 'Search results page with starting and destination cities',
      keywords: 'recherche bus départ arrivée, trouver bus depuis vers ville, voyage interurbain départ destination'
    }
  },
  {
    path: '/search/:startCity/:endCity/:date',
    name: 'searchFromToCityDate',
    component: Search,
    meta: {
      title: 'Recherche - Départ, Arrivée et Date',
      description: 'Search results page with starting city, destination city and date',
      keywords: 'recherche bus départ arrivée date, trouver bus depuis vers ville date, voyage interurbain départ destination date'
    }
  },
  {
    path: '/trip/:tripId',
    name: 'trip',
    component: Trip,
    meta: {
      title: 'Détails du trajet',
      description: 'Trip details page',
      keywords: 'détails trajet bus, informations voyage, itinéraire bus'
    }
  },
  {
    path: '/ticket/:ticketUuid',
    name: 'ticket',
    component: Ticket,
    meta: {
      title: 'eTicket',
      description: 'eTicket page',
      keywords: 'eTicket bus, billet électronique, détails billet'
    }
  },
  {
    path: '/company/:slug',
    name: 'company',
    component: Company,
    meta: {
      title: 'Détails de la compagnie',
      description: 'Company details page',
      keywords: 'détails compagnie bus, informations transporteur, avis compagnie'
    }
  },
  {
    path: '/download',
    name: 'download',
    component: Download,
    meta: {
      title: 'Télécharger l\'application',
      description: 'Téléchargez l\'application mobile EasyGO sur Android et iOS. Réservez vos voyages partout, à tout moment.',
      keywords: 'app easygo, application bus cameroun, télécharger easygo'
    }
  },
  {
    path: '/about',
    name: 'about',
    component: About,
    meta: {
      title: 'À propos',
      description: 'Découvrez EasyGO, la plateforme de réservation de voyages en bus au Cameroun. Notre mission, nos valeurs et notre équipe.',
      keywords: 'à propos easygo, qui sommes nous, mission easygo'
    }
  },
  {
    path: '/auth',
    component: Auth,
    meta: {
      title: 'Authentification',
      description: 'User authentication page'
    },
    children: [
      {
        path: '',
        redirect: { name: 'login' }
      },
      {
        path: 'login',
        name: 'login'
      },
      {
        path: 'register',
        name: 'register'
      },
      {
        path: 'forgot-password',
        name: 'forgot'
      },
      {
        path: 'validate/:phone?',
        name: 'validate'
      },
      {
        path: 'resend-validation',
        name: 'resend'
      }
    ]
  },
  {
    path: '/account',
    component: Account,
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'account',
        redirect: { name: 'account.wallet' }
      },
      {
        path: 'profile',
        name: 'account.profile',
        component: AccountProfile
      },
      {
        path: 'wallet',
        name: 'account.wallet',
        component: AccountWallet
      },
      {
        path: 'driver',
        name: 'account.driver',
        component: AccountDriver
      }
    ]
  },
  {
    path: '/admin',
    name: 'admin',
    component: Admin,
    meta: {
      requiresAuth: true,
      isAdminNav: true,
      title: 'Administration',
      description: 'Admin dashboard'
    }
  },
  {
    path: '/design',
    name: 'design',
    component: DesignSystem,
    meta: {
      title: 'Design System',
      description: 'Design System page'
    }
  },
  {
    path: '/help',
    name: 'help',
    component: Faq,
    meta: {
      title: 'Frequently Asked Questions',
      description: 'Frequently Asked Questions page',
      keywords: 'FAQ, questions fréquentes, aide, support'
    }
  }
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    }

    if (to.hash) {
      return {
        el: to.hash,
        behavior: 'smooth'
      }
    }

    return { top: 0 }
  }
})

router.beforeEach(async (to) => {
  const userStore = useUserStore()

  const requiresAuth = to.matched.some(record => record.meta.requiresAuth)

  if (!requiresAuth) {
    return true
  }

  if (!userStore.loading && !userStore.isAuthenticated && !userStore.user) {
    await userStore.fetchUser()
  }

  if (!userStore.isAuthenticated) {
    return {
      name: 'login',
      query: {
        redirect: to.fullPath
      }
    }
  }

  return true
})

// Fonction helper pour mettre à jour les meta tags
function updateMetaTags(to) {
  const title = to.meta.title ? `${DEFAULT_TITLE} - ${to.meta.title}` : DEFAULT_TITLE
  const description = to.meta.description || DEFAULT_DESCRIPTION
  const keywords = to.meta.keywords || ''
  const url = `${SITE_URL}${to.path}`

  document.title = title

  updateMetaTag('name', 'description', description)

  if (keywords) {
    updateMetaTag('name', 'keywords', keywords)
  }

  updateLink('canonical', url)

  updateMetaTag('property', 'og:title', title)
  updateMetaTag('property', 'og:description', description)
  updateMetaTag('property', 'og:url', url)

  updateMetaTag('name', 'twitter:title', title)
  updateMetaTag('name', 'twitter:description', description)
  updateMetaTag('name', 'twitter:url', url)
}

function updateMetaTag(attrName, attrValue, content) {
  let element = document.querySelector(`meta[${attrName}="${attrValue}"]`)
  if (element) {
    element.setAttribute('content', content)
  } else {
    element = document.createElement('meta')
    element.setAttribute(attrName, attrValue)
    element.setAttribute('content', content)
    document.head.appendChild(element)
  }
}

function updateLink(rel, href) {
  let element = document.querySelector(`link[rel="${rel}"]`)
  if (element) {
    element.setAttribute('href', href)
  } else {
    element = document.createElement('link')
    element.setAttribute('rel', rel)
    element.setAttribute('href', href)
    document.head.appendChild(element)
  }
}

router.afterEach((to) => {
  updateMetaTags(to)
})

export default router
